// Shopify Customer Privacy API wrapper for a headless (custom) storefront.
// Loads the consent-tracking-api feature and exposes helpers to sync the
// visitor's opt-out choice with Shopify checkout.
//
// All functions are SSR-safe and never throw.

const SHOPIFY_SHOP_DOMAIN = "63017a.myshopify.com";
const CHECKOUT_DOMAIN = "checkout.coyotecreekhatco.com";
const STOREFRONT_ROOT_DOMAIN = "coyotecreekhatco.com";
const SHOPIFY_STOREFRONT_TOKEN = "b2608e9d35062fa2df6f8eba13f611f5";
const SHOPIFY_CONSENT_JS = "https://cdn.shopify.com/shopifycloud/consent-tracking-api/v0.1/consent-tracking-api.js";

type ShopifyGlobal = {
  shop?: string;
  loadFeatures?: (
    features: Array<{ name: string; version: string }>,
    cb: (err: unknown) => void
  ) => void;
  customerPrivacy?: {
    setTrackingConsent: (
      consent: Record<string, unknown>,
      cb?: (err?: unknown) => void
    ) => void;
  };
};

declare global {
  interface Window {
    Shopify?: ShopifyGlobal;
  }
}

let loadPromise: Promise<void> | null = null;

function injectScript(src: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      if (document.querySelector(`script[data-ccp-consent="1"][src="${src}"]`)) {
        resolve();
        return;
      }
      const s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.dataset.ccpConsent = "1";
      s.onload = () => resolve();
      s.onerror = () => resolve();
      document.head.appendChild(s);
    } catch {
      resolve();
    }
  });
}

export function loadShopifyConsent(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    try {
      // The custom storefront bundle exposes window.Shopify.customerPrivacy directly.
      window.Shopify = { ...(window.Shopify ?? {}), shop: SHOPIFY_SHOP_DOMAIN };
      if (!window.Shopify.customerPrivacy) await injectScript(SHOPIFY_CONSENT_JS);
    } catch {
      // swallow
    }
  })();

  return loadPromise;
}

async function callSetTrackingConsent(consent: Record<string, unknown>): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    await loadShopifyConsent();
    const cp = window.Shopify?.customerPrivacy;
    if (!cp || typeof cp.setTrackingConsent !== "function") return;
    await new Promise<void>((resolve) => {
      try {
        cp.setTrackingConsent(
          {
            ...consent,
            headlessStorefront: true,
            storefrontRootDomain: STOREFRONT_ROOT_DOMAIN,
            checkoutRootDomain: CHECKOUT_DOMAIN,
            storefrontAccessToken: SHOPIFY_STOREFRONT_TOKEN,
          },
          () => resolve()
        );
      } catch {
        resolve();
      }
    });
  } catch {
    // swallow
  }
}

export function syncOptOutToShopify(): Promise<void> {
  return callSetTrackingConsent({
    analytics: false,
    marketing: false,
    preferences: false,
    sale_of_data: false,
  });
}

export function syncOptInToShopify(): Promise<void> {
  return callSetTrackingConsent({
    analytics: true,
    marketing: true,
    preferences: true,
    sale_of_data: true,
  });
}
